import {Locale} from '../constants'

// Only one of our locales is a prefix: `/uk` or `/us` is an ordinary path, not a language
const localePrefixRegex = new RegExp(`^/(${Object.values(Locale).join('|')})(?=/|$)`, 'i')

// The path without its locale prefix, in its original case
export const getWithoutLocale = (path: string) => path.replace(localePrefixRegex, '') || '/'

export const parseToLocaleAndCleanedPath = (
  path: string,
): {locale: Locale; cleanedPath: string} => {
  const match = path.match(localePrefixRegex)
  const locale = match ? (match[1].toLowerCase() as Locale) : Locale.en
  return {locale, cleanedPath: getWithoutLocale(path).toLowerCase()}
}

const localeRestrictions = {
  // The following pages (and all their subpages) are available only in a small subset of locales.
  // This map must match the page's own locale guard: a page that calls notFound() for a locale
  // listed here turns what should be a redirect to English into a hard 404.
  '/account-terms-and-conditions': ['en', 'es'],
  '/brand-monitoring-order': ['en', 'es'],
  '/brand-protection': ['en', 'es'],
  '/brand-protection-guide': ['en'],
  '/brand-protection-questions-and-answers': ['en'],
  '/country-guide': ['en'],
  '/domain-resolution': ['en', 'es'],
  '/engagement-letter': ['en'],
  '/for-law-firms': ['en'],
  '/industry-case-study': ['en', 'es'],
  '/industry-guide': ['en'],
  '/legal-notice': ['en', 'es'],
  '/meet-trama': ['en', 'es'],
  '/monitoring-result': ['en', 'es'],
  '/office-action': ['en', 'es'],
  '/portal': ['en', 'es'],
  '/privacy-policy': ['en', 'es'],
  '/report-brand-infringement': ['en', 'es'],
  '/report-violation': ['en', 'es'],
  '/specimen-guide': ['en'],
  '/trademark-dictionary': ['en'],
  '/trademark-disputes': ['en'],
  '/trademark-hall-of-fame': ['en'],
  '/trademark-questions-and-answers': ['en'],
  '/use-in-commerce-request': ['en', 'es'],
  '/trademark-management': ['en', 'es'],
  '/trademark-management-for-law-firms': ['en', 'es'],
  // the following pages (and all their subpages) are available in all locales (including partially supported)
  '/class-assist': ['en', 'sk', 'cs', 'es', 'de', 'fr', 'it', 'pl', 'pt'],
}

// the rest of pages are available in the following default locales
const fullySupportedLocales = ['en', 'sk', 'cs', 'es']

// where an unsupported locale is sent instead of the page's English copy
const localeRedirects: Record<string, Partial<Record<Locale, string>>> = {
  // sk and cs have no privacy policy of their own, their GDPR page covers it
  '/privacy-policy': {[Locale.sk]: '/sk/gdpr', [Locale.cs]: '/cs/gdpr'},
}

// The locales a path is served in. A page's params and locale guard should come from here, so they
// cannot drift from the redirects below.
export const getPathLocales = (path: string): Locale[] => {
  const {cleanedPath} = parseToLocaleAndCleanedPath(path)

  const localeRestriction = Object.entries(localeRestrictions).find(
    ([restrictedPath]) =>
      cleanedPath === restrictedPath || cleanedPath.startsWith(`${restrictedPath}/`),
  )

  return (localeRestriction ? localeRestriction[1] : fullySupportedLocales) as Locale[]
}

// A statically rendered page's `generateStaticParams`: one entry per locale its path is served in.
export const getPathLocaleParams = (path: string) =>
  getPathLocales(path).map((locale) => ({locale}))

// where the path sends a locale it is not served in, when that is not its English copy
export const getLocaleRedirect = (path: string, locale: Locale): string | null => {
  const {cleanedPath} = parseToLocaleAndCleanedPath(path)
  return localeRedirects[cleanedPath]?.[locale] ?? null
}

// returns new path (to be redirected to) if the input path (with locale prefix) is not supported
export const getLocaleRestrictedPath = (path: string): string | null => {
  const {locale} = parseToLocaleAndCleanedPath(path)

  if (getPathLocales(path).includes(locale)) {
    return null
  }
  // The English page is unprefixed: `/en/...` would only redirect again
  return getLocaleRedirect(path, locale) ?? getWithoutLocale(path)
}
